<template>
  <div class="space-y-6">
    <!-- Security Warning -->
    <Card class="p-6 bg-yellow-50 border-yellow-200">
      <div class="flex items-start gap-3">
        <AlertTriangle class="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
        <div>
          <h3 class="font-semibold text-yellow-800 mb-2">Test Authentication System</h3>
          <div class="text-sm text-yellow-700 space-y-1">
            <p>• Test users can only be accessed in development/staging environments</p>
            <p>• All test authentication is logged for security audit</p>
            <p>• Service role keys never reach the client - all auth is server-side</p>
            <p v-if="!isTestMode" class="font-medium text-yellow-800">
              ⚠️ Test mode is currently DISABLED
            </p>
          </div>
        </div>
      </div>
    </Card>

    <!-- Test Environment Status -->
    <Card class="p-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div :class="['w-2 h-2 rounded-full', isTestMode ? 'bg-green-500' : 'bg-red-500']" />
          <span class="text-sm font-medium">
            Test Mode: {{ isTestMode ? 'ENABLED' : 'DISABLED' }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <div class="text-xs text-gray-500">Environment: {{ environment }}</div>
          <Button
            v-if="error && error.includes('test_users table does not exist')"
            variant="primary"
            size="small"
            :disabled="applyingMigration"
            @click="applyTestAuthMigration"
          >
            <Loader2 v-if="applyingMigration" class="h-4 w-4 mr-2 animate-spin" />
            Apply Migration
          </Button>
        </div>
      </div>
    </Card>

    <!-- Add Test User -->
    <Card class="p-6">
      <h3 class="text-lg font-semibold mb-4">Create Test User</h3>

      <div class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium mb-2">Select User</label>
            <Select v-model="newTestUser.userId" placeholder="Choose a user...">
              <option v-for="user in availableUsers" :key="user.id" :value="user.id">
                {{ user.name || 'Anonymous' }} ({{ user.email }})
              </option>
            </Select>
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">Test Identifier</label>
            <Input
              v-model="newTestUser.identifier"
              type="text"
              placeholder="e.g., test-admin, test-user"
            />
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium mb-2">Description</label>
          <Input
            v-model="newTestUser.description"
            type="text"
            placeholder="Purpose of this test user"
          />
        </div>

        <Button
          :disabled="!newTestUser.userId || !newTestUser.identifier || creating"
          variant="primary"
          class="w-full"
          @click="createTestUser"
        >
          <Loader2 v-if="creating" class="h-4 w-4 mr-2 animate-spin" />
          Create Test User
        </Button>
      </div>
    </Card>

    <!-- Existing Test Users -->
    <Card class="p-6">
      <h3 class="text-lg font-semibold mb-4">Existing Test Users</h3>

      <div v-if="loading.testUsers" class="flex justify-center p-4">
        <Loader2 class="h-6 w-6 animate-spin" />
      </div>

      <div v-else-if="testUsers.length === 0" class="text-center p-8 text-gray-500">
        <Users class="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No test users created yet</p>
        <p class="text-sm">Create test users to enable secure test authentication</p>
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="testUser in testUsers"
          :key="testUser.id"
          class="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div class="flex-1">
            <div class="flex items-center gap-3 mb-1">
              <code class="text-sm font-mono bg-gray-200 px-2 py-1 rounded">
                {{ testUser.identifier }}
              </code>
              <div
                :class="[
                  'w-2 h-2 rounded-full',
                  testUser.is_active ? 'bg-green-500' : 'bg-red-500'
                ]"
              />
            </div>
            <div class="text-sm text-gray-600">
              <strong>{{ testUser.profile_name || 'Anonymous' }}</strong>
              <span v-if="testUser.profile_email"> - {{ testUser.profile_email }}</span>
            </div>
            <div v-if="testUser.description" class="text-sm text-gray-500 mt-1">
              {{ testUser.description }}
            </div>
            <div class="text-xs text-gray-400 mt-1">
              Created {{ formatDate(testUser.created_at) }}
            </div>
          </div>

          <div class="flex items-center gap-2">
            <Button
              :variant="testUser.is_active ? 'outline' : 'primary'"
              size="small"
              @click="toggleTestUser(testUser)"
            >
              {{ testUser.is_active ? 'Deactivate' : 'Activate' }}
            </Button>
            <Button
              variant="outline"
              size="small"
              class="text-red-600 border-red-300 hover:bg-red-50"
              @click="deleteTestUser(testUser)"
            >
              <Trash2 class="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>

    <!-- Test Access Logs -->
    <Card class="p-6">
      <h3 class="text-lg font-semibold mb-4">Recent Test Access Logs</h3>

      <div v-if="loading.logs" class="flex justify-center p-4">
        <Loader2 class="h-6 w-6 animate-spin" />
      </div>

      <div v-else-if="accessLogs.length === 0" class="text-center p-8 text-gray-500">
        <Shield class="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No test access logs yet</p>
        <p class="text-sm">Test authentication activity will appear here</p>
      </div>

      <div v-else class="space-y-2">
        <div
          v-for="log in accessLogs"
          :key="log.id"
          class="flex items-center justify-between p-3 text-sm border-l-4 border-blue-200 bg-blue-50"
        >
          <div class="flex items-center gap-4">
            <code class="font-mono bg-white px-2 py-1 rounded text-xs">
              {{ log.identifier }}
            </code>
            <span class="font-medium">{{ log.profile_name || 'Unknown' }}</span>
          </div>
          <div class="text-right text-xs text-gray-600">
            <div>{{ formatDate(log.accessed_at) }}</div>
            <div>{{ log.ip_address }}</div>
            <div class="text-blue-600">
              {{ log.environment }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="accessLogs.length > 0" class="mt-4 pt-4 border-t">
        <Button variant="outline" size="small" :disabled="loading.logs" @click="loadAccessLogs">
          <RefreshCw :class="['h-4 w-4 mr-2', loading.logs && 'animate-spin']" />
          Refresh Logs
        </Button>
      </div>
    </Card>

    <!-- Error/Success Messages -->
    <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <p class="text-red-800">
        {{ error }}
      </p>
    </div>
    <div v-if="success" class="bg-green-50 border border-green-200 rounded-lg p-4">
      <p class="text-green-800">
        {{ success }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { supabase } from '../../lib/supabase'
import { useUserStore } from '../../stores/user'
import { AlertTriangle, Trash2, Loader2, Users, Shield, RefreshCw } from 'lucide-vue-next'
import Card from '../ui/Card.vue'
import Button from '../ui/Button.vue'
import Select from '../ui/Select.vue'
import Input from '../ui/Input.vue'

const userStore = useUserStore()

// State
const testUsers = ref([])
const availableUsers = ref([])
const accessLogs = ref([])
const creating = ref(false)
const applyingMigration = ref(false)
const error = ref('')
const success = ref('')

const loading = ref({
  testUsers: false,
  users: false,
  logs: false
})

const newTestUser = ref({
  userId: '',
  identifier: '',
  description: ''
})

// Environment detection
const isTestMode = computed(() => {
  return (
    import.meta.env.VITE_ENABLE_TEST_MODE === 'true' ||
    import.meta.env.MODE === 'development' ||
    import.meta.env.MODE === 'test'
  )
})

const environment = computed(() => {
  return import.meta.env.MODE || 'unknown'
})

// Methods
async function checkDatabaseSetup() {
  try {
    // Try to query the test_users table to see if it exists
    const { error: tableError } = await supabase
      .from('test_users')
      .select('id')
      .limit(1)
    
    if (tableError) {
      if (tableError.message?.includes('relation') && tableError.message?.includes('does not exist')) {
        error.value = 'Database setup incomplete: test_users table does not exist. Please apply migration 20250616000001_create_test_auth_system.sql'
        return false
      }
    }
    
    return true
  } catch (err) {
    console.error('Database setup check error:', err)
    error.value = 'Unable to verify database setup: ' + (err?.message || err?.toString() || 'Unknown error')
    return false
  }
}

async function loadTestUsers() {
  try {
    loading.value.testUsers = true
    const { data, error: loadError } = await supabase.rpc('get_test_users_for_admin')

    if (loadError) throw loadError
    testUsers.value = data || []
  } catch (err) {
    console.error('Load test users error:', err)
    error.value = 'Failed to load test users: ' + (err?.message || err?.toString() || 'Unknown error occurred')
  } finally {
    loading.value.testUsers = false
  }
}

async function loadAvailableUsers() {
  try {
    loading.value.users = true
    availableUsers.value = await userStore.fetchAllUsersForAdmin()
  } catch (err) {
    console.error('Load users error:', err)
    error.value = 'Failed to load users: ' + (err?.message || err?.toString() || 'Unknown error occurred')
  } finally {
    loading.value.users = false
  }
}

async function loadAccessLogs() {
  try {
    loading.value.logs = true
    const { data, error: loadError } = await supabase.rpc('get_test_auth_logs_for_admin', {
      limit_count: 20
    })

    if (loadError) throw loadError
    accessLogs.value = data || []
  } catch (err) {
    console.error('Load logs error:', err)
    error.value = 'Failed to load access logs: ' + (err?.message || err?.toString() || 'Unknown error occurred')
  } finally {
    loading.value.logs = false
  }
}

async function createTestUser() {
  if (!newTestUser.value.userId || !newTestUser.value.identifier) return

  creating.value = true
  clearMessages()

  // Check database setup first
  const dbReady = await checkDatabaseSetup()
  if (!dbReady) {
    creating.value = false
    return
  }

  try {
    const { error: insertError } = await supabase.from('test_users').insert({
      user_id: newTestUser.value.userId,
      identifier: newTestUser.value.identifier,
      description: newTestUser.value.description || null,
      created_by: userStore.profile?.id
    })

    if (insertError) throw insertError

    success.value = 'Test user created successfully!'
    newTestUser.value = { userId: '', identifier: '', description: '' }
    await loadTestUsers()

    setTimeout(() => {
      success.value = ''
    }, 3000)
  } catch (err) {
    console.error('Create test user error:', err)
    error.value = 'Failed to create test user: ' + (err?.message || err?.toString() || 'Unknown error occurred')
  } finally {
    creating.value = false
  }
}

async function toggleTestUser(testUser) {
  try {
    const newStatus = !testUser.is_active

    const { error: updateError } = await supabase
      .from('test_users')
      .update({ is_active: newStatus })
      .eq('id', testUser.id)

    if (updateError) throw updateError

    success.value = `Test user ${newStatus ? 'activated' : 'deactivated'} successfully!`
    await loadTestUsers()

    setTimeout(() => {
      success.value = ''
    }, 3000)
  } catch (err) {
    console.error('Update test user error:', err)
    error.value = 'Failed to update test user: ' + (err?.message || err?.toString() || 'Unknown error occurred')
  }
}

async function deleteTestUser(testUser) {
  if (!confirm(`Delete test user "${testUser.identifier}"? This cannot be undone.`)) {
    return
  }

  try {
    const { error: deleteError } = await supabase.from('test_users').delete().eq('id', testUser.id)

    if (deleteError) throw deleteError

    success.value = 'Test user deleted successfully!'
    await loadTestUsers()

    setTimeout(() => {
      success.value = ''
    }, 3000)
  } catch (err) {
    console.error('Delete test user error:', err)
    error.value = 'Failed to delete test user: ' + (err?.message || err?.toString() || 'Unknown error occurred')
  }
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleString()
}

async function applyTestAuthMigration() {
  applyingMigration.value = true
  clearMessages()

  try {
    // For now, just provide helpful information about applying the migration
    error.value = 'Migration must be applied manually. Please contact an administrator to run: npx supabase db push or apply migration 20250616000001_create_test_auth_system.sql directly to the database.'
    
  } catch (err) {
    console.error('Migration error:', err)
    error.value = 'Failed to apply migration: ' + (err?.message || err?.toString() || 'Unknown error occurred')
  } finally {
    applyingMigration.value = false
  }
}

function clearMessages() {
  error.value = ''
  success.value = ''
}

// Lifecycle
onMounted(async () => {
  const dbReady = await checkDatabaseSetup()
  if (dbReady) {
    await Promise.all([loadTestUsers(), loadAvailableUsers(), loadAccessLogs()])
  } else {
    // Still load available users even if test system isn't set up
    await loadAvailableUsers()
  }
})
</script>
